import { faUpload } from "@fortawesome/pro-solid-svg-icons";
import Button, { ButtonProps } from "@mui/material/Button";
import { styled } from "@mui/material/styles";
import { Accept } from "react-dropzone";

import { SourceType } from "@/components/graphql/schema.graphql";
import type { FilepickerFile } from "@/components/ui-v2/FilePicker/types";
import { UploadableModality } from "@/constants/dropzone";
import { useDropzoneFileUpload } from "@/hooks/useDropzoneFileUpload";

import { Icon } from "../Icon/Icon";

interface UploadButtonProps extends Omit<ButtonProps, "onClick"> {
  onUploadStart?: () => void;
  onUploadSuccess?: (files: FilepickerFile[]) => void;
  modality?: UploadableModality;
  sourceType?: SourceType;
  label?: string;
  maxFiles?: number;
  accept?: Accept;
  testId?: string;
}

const Input = styled("input")({ display: "none" });

export const UploadButton = (props: UploadButtonProps) => {
  const {
    testId,
    onUploadStart,
    onUploadSuccess,
    modality,
    maxFiles,
    accept,
    sourceType,
    children = "Upload from computer",
    disabled: externalDisabled,
    ...rest
  } = props;

  const {
    getInputProps,
    isUploading,
    open: openFileUploadDialog,
  } = useDropzoneFileUpload({
    onUploadStart,
    onUploadSuccess,
    modality,
    accept,
    maxFiles,
    sourceType,
    disabled: externalDisabled,
    noClick: false,
    noKeyboard: true,
  });

  const isDisabled = externalDisabled || isUploading;

  return (
    <>
      <Input {...getInputProps()} />
      <Button
        data-testid={testId}
        onClick={openFileUploadDialog}
        color="primary"
        variant="contained"
        disabled={isDisabled}
        loading={isUploading}
        startIcon={<Icon size="large" icon={faUpload} />}
        {...rest}
      >
        {isUploading ? "Uploading..." : children}
      </Button>
    </>
  );
};
